import { Box, CircularProgress, Dialog, IconButton, InputBase, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Close, Search } from '@material-ui/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { terbebasClient } from '../terbebasApi';

// Debounced product search overlay. Matches the whole product entry
// server-side (title, description, tags, type/vendor, variant SKU).
const useStyles = makeStyles((theme: Theme) =>
createStyles({
  dialog: {
    '& .MuiDialog-paper': {
      width: '92%',
      maxWidth: '560px',
      borderRadius: '16px',
      position: 'absolute',
      top: '8%',
      margin: 0,
    },
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    borderBottom: '1px solid #ececec',
  },
  input: { flex: 1, fontSize: '15px', marginLeft: '8px' },
  resultRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#f7f7f7' },
  },
  resultImage: { width: '44px', height: '44px', objectFit: 'cover', borderRadius: '8px', marginRight: '12px' },
  resultTitle: { fontSize: '14px', fontWeight: 500 },
  resultPrice: { fontSize: '12px', fontWeight: 300, color: '#6a6a6a' },
  emptyText: { padding: '24px', textAlign: 'center', fontSize: '13px', fontWeight: 300 },
}));

function formatRp(v: number) {
  return 'Rp ' + Math.round(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export default function SearchOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const classes = useStyles();
  const history = useHistory();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<any>(null);

  useEffect(() => {
    if (!open) { setQuery(''); setResults([]); return; }
  }, [open]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      terbebasClient.get('/storefront/products', { params: { search: query.trim(), pageSize: 8 } })
        .then((res) => setResults(res.data.products ?? []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
  }, [query]);

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog} fullWidth>
      <Box className={classes.inputRow}>
        <Search fontSize="small" />
        <InputBase autoFocus id="tb-search-input" className={classes.input}
          placeholder="Cari produk…" value={query}
          onChange={(e) => setQuery(e.target.value)} />
        <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
      </Box>
      {
        loading ? <Box style={{textAlign: 'center', padding: '20px'}}><CircularProgress size={22}/></Box> :
        query.trim().length >= 2 && results.length === 0 ?
        <Typography className={classes.emptyText}>Tidak ada produk yang cocok.</Typography> :
        results.map((p) => {
          const v = p.variants?.[0];
          return (
            <Box key={p.id} className={classes.resultRow} onClick={() => {
              onClose();
              history.push('/shop/product/' + p.id);
            }}>
              <img className={classes.resultImage} alt={p.title}
                src={p.images?.[0]?.src ?? 'https://placehold.co/100x100?text=%20'} />
              <Box>
                <Typography className={classes.resultTitle}>{p.title}</Typography>
                {v && <Typography className={classes.resultPrice}>{formatRp(Number(v.price))}</Typography>}
              </Box>
            </Box>
          );
        })
      }
    </Dialog>
  );
}
